import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { number } from 'prop-types';
import React, { useRef } from 'react';

import config from '../../../config';
import PriceFilterForm from '../../../forms/PriceFilterForm/PriceFilterForm';
import { neutral } from '../../../styles/mui/colors';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import { injectIntl } from '../../../util/reactIntl';

const KEY_CODE_ESCAPE = 27;
const RADIX = 10;

const rootStyle = {
  position: 'relative',
  display: 'inline-block',
};

const buttonStyle = {
  borderRadius: 200,
  bgcolor: 'white',
  borderColor: neutral[200],
  '&:hover': {
    bgcolor: 'white',
  },
  '&:active': {
    bgcolor: 'white',
  },
  '&:focus': {
    bgcolor: 'white',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  color: 'inherit',
};

const getPriceQueryParamName = (queryParamNames) => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = (priceRange) => {
  const [minPrice, maxPrice] = priceRange
    ? priceRange.split(',').map((v) => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;
  return { [queryParamName]: value };
};

/**
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} props.id
 * @param {React.ReactNode} [props.label]
 * @param {string[]|string} props.queryParamNames
 * @param {Object} [props.initialValues]
 * @param {number} props.min
 * @param {number} props.max
 * @param {number} [props.step]
 * @param {Object} props.intl
 * @param {Object} [props.currencyConfig]
 * @param {boolean} props.isOpen
 * @param {Function} props.setIsOpen
 * @param {Function} props.onSubmit
 * @param {number} [props.contentPlacementOffset]
 */
const PriceFilterPopupComponent = ({
  rootClassName = null,
  className = null,
  id,
  label,
  queryParamNames,
  initialValues = null,
  min,
  max,
  step = number,
  intl,
  currencyConfig = config.currencyConfig,
  isOpen,
  setIsOpen,
  onSubmit,
  contentPlacementOffset = 0,
}) => {
  const filterRef = useRef(null);
  const filterContentRef = useRef(null);

  const toggleOpen = () => {
    setIsOpen();
  };

  const handleSubmit = (values) => {
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(values, priceQueryParamName));
  };

  const handleClear = () => {
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(null, priceQueryParamName));
  };

  const handleCancel = () => {
    onSubmit(initialValues);
  };

  // Open/close state is controlled by the parent via the `isOpen` prop, so blur
  // is intentionally a no-op (the original class set unused local state here).
  const handleBlur = () => {};

  const handleKeyDown = (e) => {
    // Gather all escape presses to close menu
    if (e.keyCode === KEY_CODE_ESCAPE) {
      toggleOpen();
    }
  };

  const positionStyleForContent = () => {
    if (filterRef.current && filterContentRef.current) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - filterRef.current.getBoundingClientRect().right;
      const labelWidth = filterRef.current.offsetWidth;
      const contentWidth = filterContentRef.current.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  };

  const classes = classNames(rootClassName, className);

  const priceQueryParam = getPriceQueryParamName(queryParamNames);
  const initialPrice =
    initialValues && initialValues[priceQueryParam] ? parse(initialValues[priceQueryParam]) : {};
  const { minPrice, maxPrice } = initialPrice || {};

  const hasValue = (value) => value != null;
  const hasInitialValues = initialValues && hasValue(minPrice) && hasValue(maxPrice);

  const currentLabel = hasInitialValues
    ? intl.formatMessage(
        { id: 'PriceFilter.labelSelectedButton' },
        {
          minPrice: formatCurrencyMajorUnit(intl, currencyConfig.currency, minPrice),
          maxPrice: formatCurrencyMajorUnit(intl, currencyConfig.currency, maxPrice),
        }
      )
    : label
    ? label
    : intl.formatMessage({ id: 'PriceFilter.label' });

  const contentStyle = positionStyleForContent();

  return (
    <Box
      className={classes}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      ref={filterRef}
      sx={rootClassName ? undefined : rootStyle}
    >
      <Button onClick={toggleOpen} sx={buttonStyle} variant="outlined">
        <Typography variant="body1">{currentLabel}</Typography>
      </Button>
      <PriceFilterForm
        contentRef={(node) => {
          filterContentRef.current = node;
        }}
        id={id}
        initialValues={hasInitialValues ? initialPrice : { minPrice: min, maxPrice: max }}
        intl={intl}
        isOpen={isOpen}
        max={max}
        min={min}
        onCancel={handleCancel}
        onClear={handleClear}
        onSubmit={handleSubmit}
        showAsPopup
        step={step}
        style={contentStyle}
      />
    </Box>
  );
};

PriceFilterPopupComponent.displayName = 'PriceFilterPopup';

const PriceFilterPopup = injectIntl(PriceFilterPopupComponent);

export default PriceFilterPopup;
